import React, { useEffect } from 'react';
import { Grid, InputAdornment, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  RegularRate,
  billRateGwwRate,
  billRateGwwShift,
  billRateMinReqHours,
} from 'app/models/Placement/PlacementMods';
import { payRateModStyles } from '../../PayRate/PayRateMod.styles';
import { FormInputDropdown } from '@AMIEWEB/Candidate/CandidateProfile/CandidateTabPanel/PersonalInfoTab/Form/Controls/FormInputDropdown';
import { useDispatch, useSelector } from 'react-redux';
import { selectIsUndoModal, selectbillRateGwwShift } from 'store/redux-store/placement-mods/selectors';
import { validDecimal } from 'app/helpers/numberHelper';
import { placementModsAction } from 'store/redux-store/placement-mods/slice';

export const getCalculatedAmount = (getValues, factor) => {
  const helperDiv = (
    <>
      <span>(Amount = ${getValues(factor) ? (getValues(RegularRate) * getValues(factor)).toFixed(2) : '0.00'})</span>
    </>
  );

  return helperDiv;
};

export const RateSection = ({ isExpanded, isModEdit }: { isExpanded: boolean; isModEdit: boolean }) => {
  const { t } = useTranslation();
  const { control, getValues, setValue, clearErrors } = useFormContext();
  const { classes } = payRateModStyles({ isExpanded });
  const billRateGwwShiftValue = useWatch<any>({ name: 'billRateShift' });
  const billRateShiftId = useWatch<any>({ name: 'shiftId' });
  const shiftOptions = useSelector(selectbillRateGwwShift);
  const isUndoModal = useSelector(selectIsUndoModal);
  const dispatch = useDispatch();

  const handleStatusCodeChange = value => {
    const selectedValue = shiftOptions?.find(item => item.Description === value);
    setValue('shiftId', selectedValue?.ID);
    setValue('billRateShift', value);
    if (value) {
      clearErrors('shiftId');
    }
  };

  const getDefaultOption = () => {
    const value = shiftOptions?.find(item => parseFloat(item.ID) === parseFloat(billRateShiftId));
    if (value?.ID) setValue('shiftId', value?.ID);
    setValue('billRateShift', value?.Description ?? null);
  };

  useEffect(() => {
    getDefaultOption();
  }, [shiftOptions]);

  useEffect(() => {
    if (isUndoModal) {
      getDefaultOption();
      dispatch(placementModsAction.setIsUndoModal(false));
    }
  }, [isUndoModal]);
  return (
    <>
      <Grid>
        <Controller
          name={billRateGwwRate}
          control={control}
          render={({ field: { onChange, ...rest } }) => (
            <TextField
              value={getValues(billRateGwwRate)}
              variant="filled"
              className={classes.pricingTextfields}
              label={`${t(`placement.placementMods.${billRateGwwRate}`)}`}
              InputLabelProps={{
                shrink: getValues(billRateGwwRate) ?? false,
              }}
              onChange={e => {
                if (validDecimal(e.target.value, { numberLength: 6, decimalLength: 2 })) {
                  setValue(billRateGwwRate, e.target.value);
                  onChange(e.target.value);
                }
              }}
              InputProps={{
                readOnly: !isModEdit,
                style: {
                  pointerEvents: isModEdit ? 'auto' : 'none',
                },
                startAdornment: (
                  <>
                    {getValues(billRateGwwRate) && (
                      <InputAdornment position="start">{t('currency.dollar')}</InputAdornment>
                    )}
                  </>
                ),
                sx: { fontSize: '14px' },
              }}
              {...rest}
            />
          )}
        />
      </Grid>
      <Grid className={classes.factorMarginBillRate}>
        <Controller
          control={control}
          name={'shiftId'}
          render={({ field: { onChange, ...rest } }) => {
            return (
              <FormInputDropdown
                name={'billRateShift'}
                inputValue={billRateGwwShiftValue}
                customClass={classes.pricingTextfields}
                label={`${t(`placement.placementMods.${billRateGwwShift}`)}`}
                standard={true}
                options={shiftOptions?.length > 0 ? shiftOptions : []}
                handleChange={e => {
                  onChange(e);
                  handleStatusCodeChange(e.target.value);
                }}
                readOnly={!isModEdit}
              ></FormInputDropdown>
            );
          }}
        />
      </Grid>
      <Grid className={classes.factorMarginBillRate}>
        <Controller
          name={billRateMinReqHours}
          control={control}
          render={({ field: { onChange, value, ...rest } }) => (
            <TextField
              value={getValues(billRateMinReqHours) ?? null}
              variant="filled"
              className={classes.pricingTextfields}
              label={`${t(`placement.placementMods.${billRateMinReqHours}`)}`}
              InputLabelProps={{
                shrink: getValues(billRateMinReqHours) ?? false,
              }}
              onChange={e => {
                if (validDecimal(e.target.value, { numberLength: 3, decimalLength: 2 })) {
                  setValue(billRateMinReqHours, e.target.value);
                  onChange(e.target.value);
                }
              }}
              InputProps={{
                readOnly: !isModEdit,
                style: {
                  pointerEvents: isModEdit ? 'auto' : 'none',
                },
                startAdornment: (
                  <>
                    {getValues(billRateMinReqHours) && (
                      <InputAdornment position="start">{t('currency.dollar')}</InputAdornment>
                    )}
                  </>
                ),
                sx: { fontSize: '14px' },
              }}
              {...rest}
            />
          )}
        />
      </Grid>
    </>
  );
};
